import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UsersRound } from "lucide-react";

import {
  Button,
  Card,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Text,
} from "@lagoon/ui";

import { api, ApiError, type GroupSummary } from "../api.js";
import { Failure, LoadingPanel, Notice, Working } from "../components/feedback.js";

/**
 * Groups: one list of people, attached to many sites.
 *
 * The screen is a list of expandable cards rather than a detail route: an
 * owner has a handful of groups, and adding a member should not cost a
 * navigation there and back per group.
 */
export function Groups(): React.ReactElement {
  const groups = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.listGroups()).groups,
  });

  return (
    <div className="lg:flex lg:flex-col lg:gap-7">
      <PageHeader
        title="Groups"
        description="A group is one list of people you attach to any of your sites, from a site's People tab or its toolbar. Change the group and every attached site follows immediately: removing someone here ends their access everywhere the group is attached."
      />

      <CreateGroup />

      {groups.isPending ? (
        <LoadingPanel label="Loading your groups" />
      ) : groups.isError ? (
        <Failure error={groups.error} fallback="Could not load your groups." />
      ) : groups.data.length === 0 ? (
        <EmptyState
          icon={<UsersRound />}
          title="No groups yet"
          description="Create one above, add the people, then attach it to a site."
        />
      ) : (
        <div className="lg:flex lg:flex-col lg:gap-4">
          {groups.data.map((group) => (
            <GroupCard key={group.id} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreateGroup(): React.ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createGroup(name.trim()),
    onSuccess: async () => {
      setName("");
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not create that group.");
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  return (
    <Card interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-4">
        {error === null ? null : <Notice tone="danger">{error}</Notice>}
        <form className="lg:flex lg:flex-wrap lg:gap-3" onSubmit={submit}>
          <Input
            className="lg:min-w-56 lg:flex-1"
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder='New group name, e.g. "C-Core"'
            maxLength={100}
            required
            aria-label="Name for the new group"
          />
          <Button variant="outline" type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? "Creating…" : "Create group"}
          </Button>
        </form>
      </div>
    </Card>
  );
}

/**
 * What removing one member costs, named site by site.
 *
 * The count alone is not the useful part: an owner deciding whether to press
 * Remove wants to know *which* sites go dark for that person, which is the one
 * thing the group card lists and the row itself does not.
 */
function describeRemoval(email: string | null, sites: readonly { slug: string }[]): string {
  const who = email ?? "This person";
  if (sites.length === 0) {
    return `${who} leaves this group. It is attached to no sites, so nothing changes for them today.`;
  }
  const where = sites.map((site) => site.slug).join(", ");
  const count = `${String(sites.length)} ${sites.length === 1 ? "site" : "sites"}`;
  return `${who} loses access to ${count} on their very next request: ${where}.`;
}

function GroupCard(props: { group: GroupSummary }): React.ReactElement {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /** The address a Remove was pressed for, held until it is confirmed (LGN-128). */
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["group", props.group.id],
    queryFn: () => api.getGroup(props.group.id),
    enabled: open,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["group", props.group.id] });
    await queryClient.invalidateQueries({ queryKey: ["groups"] });
  };

  const addMember = useMutation({
    mutationFn: () => api.addGroupMember(props.group.id, email.trim()),
    onSuccess: async () => {
      setEmail("");
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not add that address.");
    },
  });

  /**
   * Removing one person, confirmed and answered for (LGN-128).
   *
   * Two faults in one mutation. It ended somebody's access on every attached
   * site from a single click, while *deleting* the group spelled that blast
   * radius out in a dialog; and a refusal was silent, so a lapsed session left
   * the person in the list looking removed. Both are the same mistake: the
   * screen was not telling the truth about who can still get in.
   */
  const removeMember = useMutation({
    mutationFn: (address: string) => api.removeGroupMember(props.group.id, address),
    onSuccess: async () => {
      setConfirmingRemoval(null);
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setConfirmingRemoval(null);
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not remove that person. They still have access through this group.",
      );
    },
  });

  const removeGroup = useMutation({
    mutationFn: () => api.deleteGroup(props.group.id),
    onSuccess: async () => {
      setConfirmingDelete(false);
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (caught) => {
      setConfirmingDelete(false);
      setError(caught instanceof ApiError ? caught.message : "Could not delete the group.");
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    addMember.mutate();
  }

  const { group } = props;
  const sites = group.siteCount === 1 ? "site" : "sites";

  return (
    <Card interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <div className="lg:flex lg:flex-wrap lg:items-start lg:justify-between lg:gap-4">
          <div>
            <CardTitle as="h2">{group.name}</CardTitle>
            <Text size="caption" tone="subtle" className="lg:mt-1">
              {group.memberCount} {group.memberCount === 1 ? "person" : "people"} · attached to{" "}
              {group.siteCount} {sites}
            </Text>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            {open ? "Close" : "Manage"}
          </Button>
        </div>

        {open ? (
          <>
            {error === null ? null : <Notice tone="danger">{error}</Notice>}

            <form className="lg:flex lg:flex-wrap lg:gap-3" onSubmit={submit}>
              <Input
                className="lg:min-w-56 lg:flex-1"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                placeholder="person@example.com"
                required
                aria-label={`Email address to add to ${group.name}`}
              />
              <Button
                variant="outline"
                type="submit"
                disabled={addMember.isPending || !email.trim()}
              >
                {addMember.isPending ? "Adding…" : "Add"}
              </Button>
            </form>

            {detail.isPending ? (
              <Working label="Loading…" />
            ) : detail.isError ? (
              <Notice tone="danger">Could not load this group.</Notice>
            ) : (
              <>
                {detail.data.members.length > 0 ? (
                  <ul className="lg:flex lg:flex-col lg:gap-px lg:overflow-hidden lg:rounded-sm lg:border lg:border-hairline lg:bg-hairline">
                    {detail.data.members.map((member) => (
                      <li
                        key={member.email}
                        className="lg:flex lg:items-center lg:gap-4 lg:bg-card lg:px-4 lg:py-2.5"
                      >
                        <span className="lg:min-w-0 lg:flex-1 lg:truncate lg:text-body-sm">
                          {member.email}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={removeMember.isPending}
                          onClick={() => {
                            setConfirmingRemoval(member.email);
                          }}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Text size="sm" tone="muted">
                    Nobody in this group yet. It grants nothing until someone is.
                  </Text>
                )}

                {detail.data.sites.length > 0 ? (
                  <Text size="caption" tone="subtle">
                    Attached to: {detail.data.sites.map((site) => site.slug).join(", ")}. Removing
                    someone ends their access on all of them immediately.
                  </Text>
                ) : null}

                <div className="lg:flex lg:justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={removeGroup.isPending}
                    onClick={() => {
                      setConfirmingDelete(true);
                    }}
                  >
                    {removeGroup.isPending ? "Deleting…" : "Delete group"}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : null}

        {/*
          The same courtesy the delete dialog gets, for the same reason: this
          ends one person's access everywhere the group is attached, and the
          list of sites is the only place that blast radius is visible.
        */}
        <ConfirmDialog
          open={confirmingRemoval !== null}
          onOpenChange={(next) => {
            if (!next) setConfirmingRemoval(null);
          }}
          title="Remove this person from the group?"
          description={describeRemoval(confirmingRemoval, detail.data?.sites ?? [])}
          confirmLabel="Remove"
          destructive
          pending={removeMember.isPending}
          onConfirm={() => {
            if (confirmingRemoval !== null) removeMember.mutate(confirmingRemoval);
          }}
        />

        {/*
          One confirm, because deletion ends access on every attached site at
          once and cannot be undone in place. It was `window.confirm`, which
          could not say which sites in the browser's own voice; the dialog is
          labelled, focus-trapped and refuses to close while the delete is in
          flight.
        */}
        <ConfirmDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={`Delete "${group.name}"?`}
          description={`Everyone admitted only through it loses access to ${String(group.siteCount)} ${sites} immediately. This cannot be undone.`}
          confirmLabel="Delete group"
          destructive
          pending={removeGroup.isPending}
          onConfirm={() => {
            removeGroup.mutate();
          }}
        />
      </div>
    </Card>
  );
}
